import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { BadgeCheck, ExternalLink, Info, ShieldAlert, ShieldX } from 'lucide-react';
import {
  PERMISSIONS,
  type PublicDocumentVerificationView,
  type VerifiedDocumentLocation,
} from '@optiflow/types';
import { useLocale } from '@/hooks/useLocale';
import { formatMoney } from '@/modules/catalog/money';
import { usePermissions } from '@/hooks/usePermissions';
import { formatCalendarDate } from '@/modules/optical-orders/builder/format';
import { api } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import { formatCount } from '@/utils/format';
import { cn } from '@/utils/cn';

/** A token that cannot be one is never sent: the answer would be the same, and the request is wasted. */
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{43}$/;

/**
 * What someone sees when they scan the QR on a printed invoice or receipt.
 *
 * Public, outside the application shell, and deliberately thin: enough to confirm the document is
 * genuine and where it stands now — cancelled, voided, returned — and nothing about who bought what.
 * A signed-in member of staff who may see sales also gets a link into OptiFlow, resolved by a separate
 * authenticated request; the public answer itself never carries anything to open.
 */
export function VerifyDocumentPage() {
  const { t } = useTranslation();
  const { locale, direction } = useLocale();
  const { token = '' } = useParams<{ token: string }>();
  const wellFormed = TOKEN_SHAPE.test(token);

  const verification = useQuery({
    queryKey: ['verify', token],
    queryFn: () => api.get<PublicDocumentVerificationView>(`/verify/${token}`),
    enabled: wellFormed,
    retry: false,
  });

  const document = verification.data;
  const failed = !wellFormed || verification.isError;

  return (
    <main dir={direction} className="bg-canvas min-h-screen px-4 py-8" data-testid="verify-page">
      <div className="mx-auto w-full max-w-md">
        {wellFormed && verification.isPending ? (
          <p className="text-muted text-center text-sm">{t('common.loading')}</p>
        ) : failed || !document ? (
          <section className="border-line rounded-xl border bg-white p-6 text-center shadow-sm">
            <ShieldX className="mx-auto size-10 text-red-600" aria-hidden />
            <h1 className="text-ink mt-3 text-lg font-semibold">{t('sales.verify.notVerified')}</h1>
            <p className="text-muted mt-2 text-sm">{t('sales.verify.notVerifiedHint')}</p>
          </section>
        ) : (
          <VerifiedDocument document={document} token={token} locale={locale} />
        )}
      </div>
    </main>
  );
}

function VerifiedDocument({
  document,
  token,
  locale,
}: {
  document: PublicDocumentVerificationView;
  token: string;
  locale: ReturnType<typeof useLocale>['locale'];
}) {
  const { t } = useTranslation();
  const valid = document.status === 'VALID';
  const StatusIcon = valid ? BadgeCheck : ShieldAlert;

  return (
    <section
      className="border-line overflow-hidden rounded-xl border bg-white shadow-sm"
      aria-labelledby="verify-heading"
    >
      <div
        className={cn(
          'flex items-center gap-3 px-5 py-4',
          valid ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800',
        )}
        data-testid="verify-status"
      >
        <StatusIcon className="size-8 shrink-0" aria-hidden />
        <div className="min-w-0">
          <h1 id="verify-heading" className="text-base font-semibold">
            {t(`sales.verify.heading.${document.status}`)}
          </h1>
          <p className="text-sm">{document.storeName}</p>
        </div>
      </div>

      <div className="px-5 pt-4">
        <p className="text-muted text-xs tracking-wide uppercase">
          {t(`sales.verify.kind.${document.kind}`)}
        </p>
        <p className="text-ink font-mono text-xl font-semibold" dir="ltr">
          {document.reference}
        </p>
      </div>

      {document.notice ? (
        <p
          className="mx-5 mt-4 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="verify-notice"
        >
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
          {t(`sales.verify.notice.${document.notice}`)}
        </p>
      ) : null}

      <dl className="divide-line mt-2 divide-y px-5 text-sm">
        <Row label={t('sales.verify.store')} value={document.storeName} />
        <Row label={t('sales.verify.branch')} value={document.branchName} />
        <Row label={t('sales.verify.date')} value={formatCalendarDate(document.issuedOn, locale)} />
        <Row label={t('sales.verify.status')} value={t(`sales.verify.status_${document.status}`)} />
        {document.orderPaymentStatus ? (
          <Row
            label={t('sales.verify.orderPaymentStatus')}
            value={t(`sales.verify.payment.${document.orderPaymentStatus}`)}
          />
        ) : null}
        <Row label={t('sales.verify.items')} value={formatCount(document.itemCount, locale)} />
        <Row
          label={t('sales.verify.total')}
          value={formatMoney(document.totalMinor, locale, document.currencyCode)}
          emphasis
        />
      </dl>

      <p className="text-muted px-5 py-4 text-xs">{t('sales.verify.privacyNote')}</p>

      <StaffLink token={token} />
    </section>
  );
}

/** Shown only to a signed-in member of staff who may see sales, and only when the server resolves the document inside their own tenant. */
function StaffLink({ token }: { token: string }) {
  const { t } = useTranslation();
  const signedIn = useAuthStore((state) => Boolean(state.user));
  const { can } = usePermissions();
  const allowed = signedIn && can(PERMISSIONS.SALES_VIEW);

  const location = useQuery({
    queryKey: ['verify', 'locate', token],
    queryFn: () => api.get<VerifiedDocumentLocation>(`/sales/documents/resolve/${token}`),
    enabled: allowed,
    retry: false,
  });

  if (!allowed || !location.data) return null;

  return (
    <div className="border-line border-t px-5 py-4">
      <Link
        to={location.data.path}
        className="bg-brand-600 hover:bg-brand-700 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white"
      >
        <ExternalLink className="size-4" aria-hidden />
        {t('sales.verify.openInOptiFlow')}
      </Link>
    </div>
  );
}

function Row({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="text-muted shrink-0">{label}</dt>
      <dd className={cn('text-ink min-w-0 text-end', emphasis ? 'font-semibold' : 'font-medium')}>
        <bdi>{value}</bdi>
      </dd>
    </div>
  );
}
